import { morphSection } from '@theme/section-renderer';

/**
 * SOLE cart discount / promo-code apply + remove (cart-flow remediation #8).
 *
 * Document-delegated (not a custom element) so it keeps working after the cart section morphs and
 * re-renders the widget. Talks to POST /cart/update.js with { discount, sections, sections_url }:
 * - apply: send existing codes + the new code; if Shopify reports the entered code
 *   `applicable: false`, show the inline error and don't morph.
 * - remove: send the remaining codes (empty clears all).
 * After a successful update, morph the host section in place — hydration mode for the drawer
 * (keeps the open <dialog> intact), full mode for the cart page.
 */

const UPDATE_URL = () => window.Theme?.routes?.cart_update_url || '/cart/update.js';

/**
 * @param {object} body
 * @returns {Promise<any>}
 */
async function postUpdate(body) {
  const res = await fetch(UPDATE_URL(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

/**
 * @param {HTMLElement} root - the [data-sole-discount] wrapper
 */
function readContext(root) {
  const sectionId = root.dataset.sectionId;
  const isDrawer = root.hasAttribute('data-sole-drawer');
  const existing = Array.from(root.querySelectorAll('[data-discount-code]'))
    .map((p) => (p instanceof HTMLElement ? p.dataset.discountCode : null))
    .filter(/** @returns {code is string} */ (code) => typeof code === 'string' && code.length > 0);
  return { sectionId, isDrawer, existing };
}

/**
 * Morphs the host section from the update response, then restores focus to the input.
 * @param {string} sectionId
 * @param {any} data
 * @param {boolean} isDrawer
 */
function renderFromResponse(sectionId, data, isDrawer) {
  const html = data?.sections?.[sectionId];
  if (!html) return;
  morphSection(sectionId, html, { mode: isDrawer ? 'hydration' : 'full' });

  // After the morph the widget is a fresh node — return focus to the (now-empty) input so the
  // customer can immediately add another code or read the result.
  const newRoot = document.querySelector(`[data-sole-discount][data-section-id="${CSS.escape(sectionId)}"]`);
  const input = newRoot?.querySelector('[data-sole-discount-input]');
  if (input instanceof HTMLInputElement) input.focus();
}

/**
 * @param {HTMLElement} root
 */
async function applyDiscount(root) {
  const input = root.querySelector('[data-sole-discount-input]');
  const applyBtn = root.querySelector('[data-sole-discount-apply]');
  const errorEl = root.querySelector('[data-sole-discount-error]');
  if (!(input instanceof HTMLInputElement)) return;

  const code = input.value.trim();
  errorEl?.classList.add('hidden');
  if (!code) return;

  const { sectionId, isDrawer, existing } = readContext(root);
  if (!sectionId) return;

  // Already applied → just clear the field, no request.
  if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
    input.value = '';
    return;
  }

  applyBtn?.setAttribute('aria-busy', 'true');
  try {
    const data = await postUpdate({
      discount: [...existing, code].join(','),
      sections: [sectionId],
      sections_url: window.location.pathname,
    });

    const entered = Array.isArray(data?.discount_codes)
      ? data.discount_codes.find((d) => d?.code?.toLowerCase() === code.toLowerCase())
      : null;

    // Show the inline error if the code didn't take: not applicable to this cart, OR not echoed back
    // at all (unknown/garbage code). Either way, leave totals unchanged.
    if (!entered || entered.applicable === false) {
      input.value = '';
      errorEl?.classList.remove('hidden');
      return;
    }

    input.value = '';
    renderFromResponse(sectionId, data, isDrawer);
  } catch (e) {
    // Network/parse failure → surface the inline error rather than failing silently.
    console.error('[sole-cart-discount] apply failed', e);
    errorEl?.classList.remove('hidden');
  } finally {
    applyBtn?.removeAttribute('aria-busy');
  }
}

/**
 * @param {HTMLElement} pill
 * @param {HTMLElement} root
 */
async function removeDiscount(pill, root) {
  const code = pill.dataset.discountCode;
  if (!code) return;
  const { sectionId, isDrawer, existing } = readContext(root);
  if (!sectionId) return;
  const remaining = existing.filter((c) => c !== code);

  try {
    const data = await postUpdate({
      discount: remaining.join(','),
      sections: [sectionId],
      sections_url: window.location.pathname,
    });
    renderFromResponse(sectionId, data, isDrawer);
  } catch (e) {
    console.error('[sole-cart-discount] remove failed', e);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const applyBtn = target.closest('[data-sole-discount-apply]');
  if (applyBtn) {
    const root = applyBtn.closest('[data-sole-discount]');
    if (root instanceof HTMLElement) {
      event.preventDefault();
      applyDiscount(root);
    }
    return;
  }

  const removeBtn = target.closest('[data-sole-discount-remove]');
  if (removeBtn) {
    const pill = removeBtn.closest('.sole-discount__pill');
    const root = removeBtn.closest('[data-sole-discount]');
    if (pill instanceof HTMLElement && root instanceof HTMLElement) {
      event.preventDefault();
      removeDiscount(pill, root);
    }
  }
});

// Enter inside the discount field applies the code — and is preventDefault'd so it never submits
// the surrounding cart <form> (which would jump to checkout) on the cart page.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const input = target.closest('[data-sole-discount-input]');
  if (!input) return;
  const root = input.closest('[data-sole-discount]');
  if (root instanceof HTMLElement) {
    event.preventDefault();
    applyDiscount(root);
  }
});
